import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { apiClient } from "../../services/api";
import { LEAD_DISPATCH_ERROR_MESSAGES } from "../../services/shared";
import { DispatcherRule, Rule, SimpleRule } from "../../types";
import SimpleRuleForm, {
  SimpleRuleFormData,
  simpleFormDataToPayload,
  simpleRuleToFormData,
} from "./SimpleRuleForm";
import DispatcherRuleForm, {
  DispatcherRuleFormData,
  dispatcherFormDataToPayload,
  dispatcherRuleToFormData,
} from "./DispatcherRuleForm";
import LeadDispatchAuditLog from "./LeadDispatchAuditLog";
import DryRunDialog from "./DryRunDialog";

export default function RuleDetail() {
  const { ruleId } = useParams<{ ruleId: string }>();
  const navigate = useNavigate();

  const [rule, setRule] = useState<Rule | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [referencedBy, setReferencedBy] = useState<string[] | null>(null);

  const [dryRunOpen, setDryRunOpen] = useState(false);

  const simpleDefaults = useMemo(
    () =>
      rule && rule.type === "simple"
        ? simpleRuleToFormData(rule as SimpleRule)
        : undefined,
    [rule]
  );
  const dispatcherDefaults = useMemo(
    () =>
      rule && rule.type === "dispatcher"
        ? dispatcherRuleToFormData(rule as DispatcherRule)
        : undefined,
    [rule]
  );

  useEffect(() => {
    if (!ruleId) return;
    const fetch = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await apiClient.get(`/v1/lead-dispatch/rules/${ruleId}`);
        setRule(res.data?.data ?? null);
      } catch (e) {
        console.error(e);
        setLoadError("Impossibile caricare la regola");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [ruleId]);

  const handlePatch = async (
    payload: Record<string, unknown>
  ): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await apiClient.patch(
        `/v1/lead-dispatch/rules/${ruleId}`,
        payload
      );
      setRule(res.data?.data ?? null);
      setSaveSuccess(true);
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e)) {
        const data = e.response?.data;
        const errCode = data?.error;
        if (errCode && LEAD_DISPATCH_ERROR_MESSAGES[errCode]) {
          setSaveError(LEAD_DISPATCH_ERROR_MESSAGES[errCode]);
        } else if (data?.issues?.length) {
          setSaveError(
            data.issues
              .map(
                (i: { path: string[]; message: string }) =>
                  `${i.path.join(".")}: ${i.message}`
              )
              .join(" — ")
          );
        } else {
          setSaveError("Errore durante il salvataggio");
        }
      } else {
        setSaveError("Errore durante il salvataggio");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSimpleSubmit = async (data: SimpleRuleFormData) => {
    await handlePatch(simpleFormDataToPayload(data, { forUpdate: true }));
  };

  const handleDispatcherSubmit = async (data: DispatcherRuleFormData) => {
    await handlePatch(
      dispatcherFormDataToPayload(data, { forUpdate: true })
    );
  };

  const handleDelete = async () => {
    if (!rule) return;
    setDeleting(true);
    setDeleteError(null);
    setReferencedBy(null);
    try {
      await apiClient.delete(`/v1/lead-dispatch/rules/${rule.id}`);
      navigate("/lead-dispatch/rules");
    } catch (e) {
      console.error(e);
      if (axios.isAxiosError(e) && e.response?.status === 422) {
        const errCode = e.response.data?.error;
        if (errCode === "REFERENCED_BY_DISPATCHER") {
          setReferencedBy(e.response.data?.data?.referenced_by ?? []);
          setDeleteError(LEAD_DISPATCH_ERROR_MESSAGES.REFERENCED_BY_DISPATCHER);
        } else {
          setDeleteError(
            LEAD_DISPATCH_ERROR_MESSAGES[errCode] ??
              "Impossibile eliminare la regola"
          );
        }
      } else {
        setDeleteError("Errore durante l'eliminazione");
      }
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError || !rule) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", py: 3 }}>
        <Alert severity="error">{loadError ?? "Regola non trovata"}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", py: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          {rule.displayName}
        </Typography>
        <Chip
          label={rule.type === "simple" ? "Simple" : "Dispatcher"}
          size="small"
          color={rule.type === "simple" ? "primary" : "secondary"}
          variant="outlined"
        />
        <Chip
          label={rule.active ? "Attiva" : "Disattiva"}
          size="small"
          color={rule.active ? "success" : "default"}
          variant="outlined"
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="outlined"
          onClick={() => setDryRunOpen(true)}
        >
          Test dispatch (dry-run)
        </Button>
      </Stack>

      <Typography
        variant="caption"
        color="text.secondary"
        fontFamily="monospace"
        sx={{ display: "block", mb: 3 }}
      >
        ID: {rule.id} · creata: {new Date(rule.created_at).toLocaleString("it-IT")} · aggiornata: {new Date(rule.updated_at).toLocaleString("it-IT")}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Regola aggiornata con successo
          </Alert>
        )}
        {rule.type === "simple" ? (
          <SimpleRuleForm
            defaultValues={simpleDefaults}
            onSubmit={handleSimpleSubmit}
            isLoading={saving}
            submitLabel="Salva modifiche"
            submitError={saveError}
          />
        ) : (
          <DispatcherRuleForm
            defaultValues={dispatcherDefaults}
            onSubmit={handleDispatcherSubmit}
            isLoading={saving}
            submitLabel="Salva modifiche"
            submitError={saveError}
          />
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <LeadDispatchAuditLog ruleId={rule.id} />
      </Paper>

      <Paper
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "error.main",
        }}
      >
        <Typography
          variant="subtitle1"
          color="error"
          sx={{ fontWeight: 700, mb: 1 }}
        >
          Zona pericolosa
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="body2" fontWeight={600}>
              Elimina questa regola
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Operazione irreversibile. Se la regola è referenziata da un
              dispatcher, va prima rimossa dai dispatcher.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              setDeleteOpen(true);
              setDeleteError(null);
              setReferencedBy(null);
            }}
          >
            Elimina regola
          </Button>
        </Stack>
      </Paper>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Elimina regola</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare la regola{" "}
            <strong>{rule.displayName}</strong>?
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
              {referencedBy && referencedBy.length > 0 && (
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                  {referencedBy.map((id) => (
                    <li key={id}>
                      <Typography variant="body2" fontFamily="monospace">
                        {id}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>
            Chiudi
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            disabled={deleting || !!referencedBy}
          >
            {deleting ? "Eliminazione..." : "Elimina"}
          </Button>
        </DialogActions>
      </Dialog>

      <DryRunDialog
        open={dryRunOpen}
        onClose={() => setDryRunOpen(false)}
        ruleId={rule.id}
        ruleType={rule.type}
      />
    </Box>
  );
}
